import React, { useCallback } from "react";
import * as strings from "../../../strings";
import * as ProgramUtils from "../../../lib/util/ProgramUtils";
import * as EnvironmentUtils from "../../../lib/util/EnvironmentUtils";
import Button from "../../common/Button/Button";
import Select from "../../form/Select/Select";
import * as UserSelectors from "../../../store/User/User.selector";
import * as FleetSelectors from "../../../store/Fleet/Fleet.selector";
import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import Text from "../../common/Text/Text";
import * as GlobalActions from "../../../store/Global/Global.action";
import { DEMO_FLEET_ID } from "../../../lib/Flags";
import { useDispatch, useSelector } from "../../../lib/hook/ReduxHooks";
import AsyncState from "react-async-stateful";

export interface MultiFleetBannerProps {
	program: AsyncState<any>;
	programId?: string | undefined;
}

const MultiFleetBanner = (props: MultiFleetBannerProps) => {
	const { programId, program } = props;

	const { fleetId, loggedInUser, allFleets } = useSelector((state) => ({
		fleetId: UserSelectors.selectCurrentScopeFleetId(state),
		loggedInUser: UserSelectors.selectLoggedInUser(state),
		allFleets: FleetSelectors.getAllFleets(state).value,
	}));

	const dispatch = useDispatch();

	const handleSelectFleet = useCallback(
		(option) => {
			if (fleetId !== option.value) {
				NavigationUtils.go("/home");
				dispatch(
					GlobalActions.setScope({
						fleetId: option.value,
						group: null,
					}),
				);
			}
		},
		[dispatch, fleetId],
	);

	const handleOverview = useCallback(
		(e: React.MouseEvent) => {
			if (program.value && program.value.plans) {
				NavigationUtils.link("/program/overview", e);
			}
		},
		[program.value],
	);

	const branding =
		program.value?.branding || ProgramUtils.getProgramBranding(programId);

	let env = EnvironmentUtils.getAppEnvironment();
	env = env === "local" ? "qa" : env;
	const logoUrl = `https://assets${
		env !== "prod" ? `-${env}` : ""
	}.brightmile.io/${branding.logoUrl}`;

	return loggedInUser?.additionalFleets ? (
		<div
			className={"MultiFleetBanner"}
			style={{
				background: branding.secondaryColor,
				color: branding.primaryColor,
			}}
		>
			<div
				className={"MultiFleetBanner__content"}
				style={{ color: branding.textColor }}
			>
				<button className={"MultiFleetBanner__start"} onClick={handleOverview}>
					<div className={"MultiFleetBanner__branding"}>
						<div className={"MultiFleetBanner__start-backdrop"} />
						<div className={"MultiFleetBanner__title"}>
							<img
								alt={"program logo"}
								className={"MultiFleetBanner__logo"}
								src={logoUrl}
							/>
						</div>
						<div
							className={"MultiFleetBanner__highlight"}
							style={{
								background: branding.primaryColor,
								borderColor: branding.highlightColor,
							}}
						/>
					</div>
				</button>
				<Text className={"MultiFleetBanner__details"}>
					{strings.PROGRAM_ACCESS_MORE_FLEETS.map(
						(text) =>
							(
								<>
									{text}{" "}
									<a href={"mailto:axa@brightmile.io"}>
										<b>axa@brightmile.io</b>
									</a>
								</>
							) as any,
					)}
				</Text>
				<div
					className={"MultiFleetBanner__controls"}
					style={{ caretColor: branding.highlightColor }}
				>
					{program.value && program.value.plans && (
						<Button onClick={handleOverview}>
							{program.value?.name || branding.name}
						</Button>
					)}
					<Select
						loading={program.pending}
						value={fleetId}
						onChange={handleSelectFleet}
						required={true}
						fixed={true}
						compact={true}
						options={
							allFleets!
								.filter((fleet) => fleet.id !== DEMO_FLEET_ID)
								.map((fleet) => ({
									value: fleet.id,
									label: fleet.name,
								})) || []
						}
					/>
				</div>
			</div>
		</div>
	) : null;
};

export default MultiFleetBanner;
